import { normalizeEmotion } from "../../utils/emotion";
import { clamp, hash01 } from "./chunk-0.jsx";
import { OledPixelEye } from "./chunk-15.jsx";
export function OledIrisEye({
  side,
  eyeRadius,
  pupilScale,
  openness,
  outline,
  outlineWidth,
  irisColor,
  irisRing,
  pupilShape,
  pupilPulse,
  gazeName,
  tilt = 0,
  blinkPulse,
  seedKey
}) {
  const isLeft = side === 'left';
  const rot = (tilt || 0) * (isLeft ? -1 : 1);
  const close = clamp(1 - openness, 0, 0.95);
  const borderRadius = `${Math.round(eyeRadius * 100)}%`;
  const seed = hash01(seedKey || `oled-iris:${side}`);
  const blinkDurationS = 2.8 + seed * 2.0;
  const gazeDurationS = 3.6 + seed * 2.2;
  const blinkDuration = `${blinkDurationS.toFixed(2)}s`;
  const gazeDuration = `${gazeDurationS.toFixed(2)}s`;
  const blinkDelay = `${(-seed * blinkDurationS).toFixed(2)}s`;
  const gazeDelay = `${(-seed * gazeDurationS).toFixed(2)}s`;
  const iris = String(irisColor || '#111827');
  const gazeAnim = gazeName || 'oledGaze';
  const pupilKind = String(pupilShape || 'round');
  const pupilAnim = pupilPulse ? 'oledPupilPulse 1.9s ease-in-out infinite' : undefined;
  const pupilSize = `${Math.round(pupilScale * 54)}%`;
  const pupilClip = pupilKind === 'star' ? 'polygon(50% 0%, 62% 26%, 90% 35%, 70% 55%, 76% 84%, 50% 70%, 24% 84%, 30% 55%, 10% 35%, 38% 26%)' : undefined;
  return <div className="relative w-[28%] h-[58%] flex items-center justify-center">
      <div className="relative w-[76%] h-[64%] bg-white/95 overflow-hidden" style={{
      borderRadius,
      transform: `rotate(${rot}deg)`,
      outline: outline ? `${Math.max(1, Number(outlineWidth) || 2)}px solid rgba(255,255,255,0.55)` : undefined,
      outlineOffset: outline ? '2px' : undefined
    }}>
	        {pupilScale > 0 ? <div className="absolute inset-0 pointer-events-none" style={{
        animationName: gazeAnim,
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        animationDuration: gazeDuration,
        animationDelay: gazeDelay
      }}>
	            <div className="absolute left-1/2 top-1/2 rounded-full" style={{
          width: `${Math.round(Math.max(pupilScale, 0.25) * 86)}%`,
          height: `${Math.round(Math.max(pupilScale, 0.25) * 86)}%`,
          transform: 'translate(-50%,-50%)',
          background: `radial-gradient(circle at 35% 30%, rgba(255,255,255,0.55) 0%, ${iris} 28%, rgba(15,23,42,0.95) 100%)`,
          opacity: irisRing ? 0.95 : 0.55
        }} />
	            <div className="absolute left-1/2 top-1/2 bg-black" style={{
          width: pupilKind === 'slit' ? `${Math.max(8, Math.round(pupilScale * 22))}%` : pupilSize,
          height: pupilKind === 'slit' ? `${Math.max(34, Math.round(pupilScale * 70))}%` : pupilSize,
          borderRadius: '999px',
          clipPath: pupilClip,
          transform: 'translate(-50%,-50%)',
          animation: pupilAnim
        }} />
            <div className="absolute left-[24%] top-[20%] w-[18%] aspect-square rounded-full bg-white/35" />
          </div> : null}
        <div className="absolute left-0 right-0 top-0 bg-black" style={{
        height: `${close * 50}%`
      }} />
        <div className="absolute left-0 right-0 bottom-0 bg-black" style={{
        height: `${close * 50}%`
      }} />

        <div className="absolute inset-0 bg-black origin-center pointer-events-none" style={{
        transform: 'scaleY(0)',
        animationName: 'oledBlink',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        animationDuration: blinkDuration,
        animationDelay: blinkDelay
      }} />
        <div className={`absolute inset-0 bg-black origin-center pointer-events-none transition-transform duration-150 ease-in-out ${blinkPulse ? 'scale-y-100' : 'scale-y-0'}`} />
      </div>
    </div>;
}
export function OledPixelEyes({
  emotion,
  profile,
  blinkPulse
}) {
  const e = normalizeEmotion(emotion);
  const gap = profile.gap ?? 12;
  const size = Number(profile.pixelSize) > 0 ? Number(profile.pixelSize) : 8;
  const cols = Number(profile.cols) > 0 ? Number(profile.cols) : 10;
  const rows = Number(profile.rows) > 0 ? Number(profile.rows) : 6;
  const on = profile.on || 'bg-white/95';
  const gazeAnim = profile.gaze || 'oledGaze';
  const pattern = (() => {
    switch (e) {
      case 'sleepy':
        return {
          kind: 'dash'
        };
      case 'sad':
      case 'crying':
        return {
          kind: 'dashDown'
        };
      case 'laughing':
        return {
          kind: 'chevron'
        };
      case 'surprised':
        return {
          kind: 'round'
        };
      case 'angry':
        return {
          kind: 'slant'
        };
      case 'confident':
        return {
          kind: 'slantSoft'
        };
      case 'confused':
        return {
          kind: 'zigzag'
        };
      case 'thinking':
        return {
          kind: 'dots'
        };
      case 'winking':
        return {
          kind: 'wink'
        };
      case 'cool':
        return {
          kind: 'bar'
        };
      case 'embarrassed':
        return {
          kind: 'round',
          blush: true
        };
      case 'delicious':
        return {
          kind: 'dots',
          drool: true
        };
      case 'silly':
        return {
          kind: 'x'
        };
      case 'relaxed':
        return {
          kind: 'half'
        };
      case 'loving':
        return {
          kind: 'heart'
        };
      default:
        return {
          kind: 'rect'
        };
    }
  })();
  return <div className="w-full h-full flex items-center justify-center">
	    <div className="relative w-[78%] h-[56%] flex items-center justify-center" style={{
      gap: `${gap}%`
    }}>
	      <OledPixelEye cols={cols} rows={rows} size={size} on={on} pattern={pattern} side="left" blinkPulse={blinkPulse} seedKey={`oled-pixel:${emotion}`} gazeAnim={gazeAnim} />
	      <OledPixelEye cols={cols} rows={rows} size={size} on={on} pattern={pattern} side="right" blinkPulse={blinkPulse} seedKey={`oled-pixel:${emotion}`} gazeAnim={gazeAnim} />
        {pattern.blush ? <>
            <div className="absolute left-[18%] bottom-[-10%] w-[14%] h-[10%] rounded-full bg-pink-500/25 blur-[1px]" />
            <div className="absolute right-[18%] bottom-[-10%] w-[14%] h-[10%] rounded-full bg-pink-500/25 blur-[1px]" />
          </> : null}
        {pattern.drool ? <div className="absolute left-1/2 bottom-[-12%] w-[5%] h-[16%] -translate-x-1/2 bg-cyan-200/70 rounded-b-full rounded-t-[45%]" /> : null}
      </div>
    </div>;
}
